import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Merkt sich die Scroll-Position je Seite und stellt sie beim Zurückkommen wieder her.
 *
 * ## Warum es das gibt
 *
 * Wer in einer langen Liste nach unten scrollt, einen Datensatz öffnet und zurückgeht, stand
 * bis dahin **immer wieder ganz oben** — gemeldet von OEZ Trade am 02.09.2026: „wenn man iwo
 * drin ist, und auf zurück geht geht er direkt an den Anfang nicht wo man zuletzt war". Bei
 * 4.754 Kunden oder mehreren tausend Artikeln heisst das: jedes Mal neu suchen.
 *
 * ## 🚨 Drei Eigenheiten dieser Anwendung, die die Lösung bestimmen
 *
 * 1. **Es scrollt nicht das Fenster.** Der Inhalt liegt in `<main class="… overflow-auto">`
 *    (siehe `components/layout/Layout.tsx`); `window.scrollY` ist hier dauerhaft 0. Jede
 *    Lösung über das Fenster wäre wirkungslos, ohne dass etwas auffällt.
 * 2. **Kein Data-Router.** Die App benutzt `<BrowserRouter>` + `<Routes>`, also steht React
 *    Routers eingebautes `<ScrollRestoration>` nicht zur Verfügung.
 * 3. **Der Rückweg ist meist ein PUSH, kein POP.** Die Detailseiten navigieren auf einen festen
 *    Pfad zurück (`navigate('/orders')` in `OrderDetail.tsx`), nicht über die Verlaufstaste.
 *    Eine Wiederherstellung, die am History-Key hängt, griffe dort nie — deshalb wird die
 *    Position **pro Pfad** abgelegt.
 *
 * ## Warum der Beobachter
 *
 * Beim Betreten ist die Liste noch leer: Die Daten kommen erst nach dem ersten Rendern, der
 * Container ist kurz zu kurz und `scrollTop` lässt sich gar nicht setzen. Der `ResizeObserver`
 * zieht die Position nach, sobald der Inhalt hoch genug ist — und hört danach auf. Ein
 * Dauerbeobachter würde später, wenn jemand selbst gescrollt hat und noch etwas nachlädt,
 * ungefragt zurückspringen.
 */

/** Wie lange nach dem Seitenwechsel höchstens auf den Inhalt gewartet wird. */
const WARTEZEIT_MS = 1500;

/** Positionen unterhalb dieser Schwelle sind es nicht wert, gemerkt zu werden. */
const MINDEST_POSITION = 40;

/**
 * Setzt `scrollTop` auf `ziel`, sobald der Inhalt hoch genug ist (V-1613: aus dem Hook
 * herausgezogen, damit auch die Kundenliste ihren eigenen Tabellen-Container wiederherstellen kann).
 *
 * Erreichbar heisst: `scrollHeight - clientHeight >= ziel`. Vorher würde der Browser den Wert
 * stillschweigend auf das Maximum kürzen — und die Position wäre für immer verloren. Nach
 * `wartezeitMs` wird nicht mehr gesprungen: Wer bis dahin selbst gescrollt hat, soll nicht
 * plötzlich woanders landen.
 *
 * @returns eine Funktion, die Beobachter und Frist sofort beendet.
 */
export function scrolleSobaldErreichbar(
  el: HTMLElement,
  ziel: number,
  optionen: { wartezeitMs: number },
): () => void {
  let beobachter: ResizeObserver | null = null;
  let abbruch: ReturnType<typeof setTimeout> | null = null;

  const aufraeumen = () => {
    beobachter?.disconnect();
    beobachter = null;
    if (abbruch) {
      clearTimeout(abbruch);
      abbruch = null;
    }
  };

  const versuchen = () => {
    if (el.scrollHeight - el.clientHeight >= ziel) {
      el.scrollTop = ziel;
      aufraeumen();
      return true;
    }
    return false;
  };

  if (!versuchen()) {
    beobachter = new ResizeObserver(() => { versuchen(); });
    beobachter.observe(el);
    abbruch = setTimeout(aufraeumen, optionen.wartezeitMs);
  }
  return aufraeumen;
}

const schluesselFuer = (pfad: string): string => `scrollpos:${pfad}`;

export function useScrollWiederherstellung(container: React.RefObject<HTMLElement | null>): void {
  const location = useLocation();
  const pfad = location.pathname;
  // Der zuletzt gemerkte Wert des VORHERIGEN Pfads — beim Wechsel wird er weggeschrieben.
  const letztePosition = useRef(0);

  useEffect(() => {
    const el = container.current;
    if (!el) return;

    letztePosition.current = 0;

    const beimScrollen = () => {
      letztePosition.current = el.scrollTop;
    };
    el.addEventListener('scroll', beimScrollen, { passive: true });

    // --- Wiederherstellen ---------------------------------------------------
    let ziel = 0;
    try {
      ziel = Number(sessionStorage.getItem(schluesselFuer(pfad)) ?? 0);
    } catch {
      ziel = 0; // privater Modus / gesperrter Speicher
    }

    let abbrechen: (() => void) | null = null;
    if (ziel >= MINDEST_POSITION) {
      abbrechen = scrolleSobaldErreichbar(el, ziel, { wartezeitMs: WARTEZEIT_MS });
    }

    // --- Merken beim Verlassen ---------------------------------------------
    return () => {
      el.removeEventListener('scroll', beimScrollen);
      abbrechen?.();

      try {
        if (letztePosition.current >= MINDEST_POSITION) {
          sessionStorage.setItem(schluesselFuer(pfad), String(Math.round(letztePosition.current)));
        } else {
          // Oben stehengeblieben: Eintrag entfernen, statt eine 0 zu hinterlassen.
          sessionStorage.removeItem(schluesselFuer(pfad));
        }
      } catch {
        // Speicher nicht verfügbar — dann eben ohne Merken.
      }
    };
  }, [pfad, container]);
}
